/**
 * Wrong-PIN counting for the lock screen (security plan Phase 4).
 *
 * The rule is unchanged: after 5 wrong PINs in a row the PIN pad pauses for 30
 * seconds, then the count starts again from zero; a correct PIN (or a biometric
 * unlock) clears it. "Log out" stays available the whole time, so a nurse who
 * forgot her PIN is never stuck.
 *
 * What changed is where the count lives. It used to be React state, so killing
 * the app wiped it and gave anyone holding the phone a fresh 5 tries per
 * restart, with no pause ever. Now it is saved in the device's secure store
 * (lib/app-lock.ts), so a restart continues where it left off.
 *
 * Persisting a deadline adds one new way to get stuck that the in-memory
 * version never had: a device clock that jumps backwards would turn a 30-second
 * pause into hours. `normalizeAttempts` caps any saved pause at 30 seconds from
 * now, so the worst case is exactly today's pause.
 *
 * Import-free (no React Native) so the rules are unit-tested under plain Node:
 * src/__tests__/app-lock-attempts.test.ts.
 */

/** Wrong PINs in a row before the pause. Same value as before this change. */
export const PIN_ATTEMPTS_BEFORE_DELAY = 5;
/** Length of the pause. Same value as before this change. */
export const PIN_DELAY_MS = 30_000;

export interface PinAttemptState {
    /** Wrong PINs since the last success or the last pause. */
    failures: number;
    /** Epoch ms until which PIN entry is paused; 0 when not paused. */
    blockedUntil: number;
}

export const NO_PIN_ATTEMPTS: PinAttemptState = { failures: 0, blockedUntil: 0 };

/**
 * Makes any state safe to act on: bad or missing values become "no failures",
 * a negative count becomes 0, and a pause can never end more than PIN_DELAY_MS
 * from `now` (device clock moved backwards) or be shown once it has passed.
 */
export function normalizeAttempts(state: unknown, now: number): PinAttemptState {
    if (!state || typeof state !== "object") {
        return { ...NO_PIN_ATTEMPTS };
    }
    const s = state as Partial<Record<keyof PinAttemptState, unknown>>;
    const failures =
        typeof s.failures === "number" && Number.isFinite(s.failures)
            ? Math.min(Math.max(0, Math.floor(s.failures)), PIN_ATTEMPTS_BEFORE_DELAY - 1)
            : 0;
    let blockedUntil =
        typeof s.blockedUntil === "number" && Number.isFinite(s.blockedUntil) ? s.blockedUntil : 0;
    if (blockedUntil <= now) {
        blockedUntil = 0;
    } else if (blockedUntil > now + PIN_DELAY_MS) {
        blockedUntil = now + PIN_DELAY_MS;
    }
    return { failures, blockedUntil };
}

/** Milliseconds left in the pause, 0 when PIN entry is allowed. */
export function remainingDelayMs(state: PinAttemptState, now: number): number {
    return Math.max(0, normalizeAttempts(state, now).blockedUntil - now);
}

/**
 * The state after one more wrong PIN: the count goes up, and the 5th one starts
 * the 30-second pause and resets the count, exactly as the lock screen did.
 */
export function recordWrongPin(state: PinAttemptState, now: number): PinAttemptState {
    const s = normalizeAttempts(state, now);
    const failures = s.failures + 1;
    if (failures >= PIN_ATTEMPTS_BEFORE_DELAY) {
        return { failures: 0, blockedUntil: now + PIN_DELAY_MS };
    }
    return { failures, blockedUntil: s.blockedUntil };
}

/**
 * Combines what was saved before the app was killed with anything counted in
 * this session before the saved copy finished loading: never fewer failures,
 * never a shorter pause than either says.
 */
export function mergeAttempts(
    saved: PinAttemptState,
    current: PinAttemptState,
    now: number
): PinAttemptState {
    const a = normalizeAttempts(saved, now);
    const b = normalizeAttempts(current, now);
    return {
        failures: Math.max(a.failures, b.failures),
        blockedUntil: Math.max(a.blockedUntil, b.blockedUntil)
    };
}

/** Serialized form for the secure store. */
export function serializeAttempts(state: PinAttemptState): string {
    return JSON.stringify({ failures: state.failures, blockedUntil: state.blockedUntil });
}

/** Reads the stored form; anything unreadable counts as no failures. */
export function parseAttempts(raw: string | null | undefined, now: number): PinAttemptState {
    if (!raw) {
        return { ...NO_PIN_ATTEMPTS };
    }
    try {
        return normalizeAttempts(JSON.parse(raw), now);
    } catch {
        return { ...NO_PIN_ATTEMPTS };
    }
}
